import { ObjectiveChanges } from '@/components/objectives/ObjectiveChanges';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Pencil, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ObjectiveForm } from '@/components/objectives/ObjectiveForm';
import { ObjectiveProgressLine } from '@/components/objectives/ObjectiveProgressLine';
import { useObjectiveProgress } from '@/lib/objective-progress';
import { useRequestedObjective } from '@/components/objectives/useRequestedObjective';
import { useWorkspaceObjectives } from '@/lib/workspace-objectives';
import { useRegisterAppAssistantContext } from '@/lib/app-assistant-context';
import {
  canEditWorkspaceObjective,
  hasCurrentWorkspaceObjectiveRef,
  type WorkspaceObjective,
} from '@shared/types/workspace-objectives';
import { cn } from '@/lib/utils';
import { listActivitySubjects } from '@/lib/activity-feed';
import { getSubjectWorkActions } from '@/lib/work-actions';
import type { ActivitySubjectCard } from '@shared/types/activity-feed';
import { sweepBusinessVocabulary } from '@/lib/ui-editorial-sweeper';
import { useThreadStateRealtime } from '@/components/activity/useThreadStateRealtime';

/**
 * The company's and the team's objectives (08/10/2026: moved from the
 * « Objectifs » page into Profil — step ② of the qualification for managers,
 * a tab of its own for members — since the analysis is where they are set).
 */
export const ObjectivesPanel = () => {
  const { objectives, measures, members, userId, isManager, loading, error, refresh, setStatus } =
    useWorkspaceObjectives();
  const [editing, setEditing] = useState<WorkspaceObjective | 'new' | null>(null);
  const clearRequestedObjective = useRequestedObjective(setEditing);
  const [showHistory, setShowHistory] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [subjects, setSubjects] = useState<ActivitySubjectCard[]>([]);
  const [contributionsUnavailable, setContributionsUnavailable] = useState(false);
  const loadContributions = useCallback(async () => {
    try {
      const response = await listActivitySubjects();
      setSubjects(response.subjects);
      setContributionsUnavailable(false);
    } catch {
      setContributionsUnavailable(true);
    }
  }, []);
  useEffect(() => {
    let active = true;
    void listActivitySubjects()
      .then((response) => { if (active) setSubjects(response.subjects); })
      .catch(() => { if (active) setContributionsUnavailable(true); });
    return () => { active = false; };
  }, []);
  useThreadStateRealtime({ enabled: true, onRefresh: loadContributions });
  const visible = useMemo(
    () =>
      objectives.filter((goal) =>
        showHistory ? goal.status !== 'active' : goal.status === 'active'
      ),
    [objectives, showHistory],
  );
  const measuredIds = visible.filter((goal) => measures[goal.id]).map((goal) => goal.id);
  const { progress } = useObjectiveProgress(
    measuredIds,
    measuredIds.map((id) => measures[id]?.updated_at ?? '').join('|'),
    measuredIds.filter((id) => measures[id]?.source !== 'app_status'),
  );
  const context = useMemo(() => ({
    surface: 'home' as const,
    routePath: '/profil?tab=objectives',
    routeLabel: 'Objectifs',
    visibleSummary: visible.map((goal) => `${goal.title} — ${goal.success_criteria}`).join('\n')
      .slice(0, 4000),
    visibleEntities: visible.map((goal) => goal.title).slice(0, 15),
  }), [visible]);
  useRegisterAppAssistantContext(context);
  const nameOf = (id: string | null) =>
    id === userId
      ? 'Vous'
      : members.find((member) => member.user_id === id)?.display_name || 'Membre sans nom';
  const contributionsFor = (goal: WorkspaceObjective) => {
    return subjects.flatMap((subject) =>
      getSubjectWorkActions(subject)
        .filter((work) => hasCurrentWorkspaceObjectiveRef(work.evidenceRefs, goal))
        .map((work) => ({ subject, work })),
    ).slice(0, 5);
  };
  const updateStatus = async (goal: WorkspaceObjective, status: WorkspaceObjective['status']) => {
    setSavingId(goal.id);
    setMutationError(null);
    try {
      await setStatus(goal, status);
    } catch (cause) {
      setMutationError(
        cause instanceof Error ? cause.message : 'Impossible de modifier cet objectif.',
      );
    } finally {
      setSavingId(null);
    }
  };
  const sections = [
    { title: 'Entreprise', goals: visible.filter((goal) => goal.scope === 'company') },
    {
      title: 'Mes objectifs',
      goals: visible.filter((goal) => goal.scope === 'individual' && goal.owner_user_id === userId),
    },
    ...(isManager
      ? [{
        title: 'Objectifs des collaborateurs',
        goals: visible.filter((goal) =>
          goal.scope === 'individual' && goal.owner_user_id !== userId
        ),
      }]
      : []),
  ];
  return (
    <div className='max-w-5xl'>
      <header className='mb-6 flex flex-wrap items-start justify-between gap-4'>
        <div>
          <h2 className='font-display text-xl font-semibold text-domino-ink'>Objectifs</h2>
          <p className='mt-1 max-w-xl text-sm leading-6 text-domino-inkSoft'>
            Domino s’appuie sur ces objectifs pour proposer vos priorités. Les modifier entraîne un nouvel examen des travaux concernés.
          </p>
        </div>
        {!editing && (
          <Button
            onClick={() => setEditing('new')}
            disabled={loading || Boolean(error)}
            className='gap-2'
          >
            <Plus className='h-4 w-4' />Définir un objectif
          </Button>
        )}
      </header>
      {editing && (
        <ObjectiveForm
          key={editing === 'new' ? 'new' : editing.id}
          objective={editing === 'new' ? undefined : editing}
          onClose={() => { setEditing(null); clearRequestedObjective(); }}
        />
      )}
      <div className='mb-6 flex items-center justify-between gap-3'>
        <div className='flex gap-4 border-b border-domino-cardBorder'>
          {[{ value: false, label: 'En cours' }, { value: true, label: 'Historique' }].map((
            tab,
          ) => (
            <button
              key={tab.label}
              type='button'
              aria-pressed={showHistory === tab.value}
              onClick={() => setShowHistory(tab.value)}
              className={cn(
                'border-b-2 py-2 text-sm',
                showHistory === tab.value
                  ? 'border-domino-aubergine font-semibold text-domino-ink'
                  : 'border-transparent text-domino-inkSoft',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Link to='/tasks' className='inline-flex items-center gap-1 text-sm text-domino-aubergine'>
          Mes priorités<ArrowUpRight className='h-4 w-4' />
        </Link>
      </div>
      {(error || mutationError) && (
        <div
          role='alert'
          className='mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800'
        >
          {error || mutationError}
          {error && <Button variant='ghost' onClick={() => void refresh()}>Réessayer</Button>}
        </div>
      )}
      {loading
        ? (
          <p role='status' className='py-8 text-sm text-domino-inkSoft'>
            Chargement des objectifs…
          </p>
        )
        : !error && (
          <div className='space-y-8'>
            {sections.filter((section) => section.goals.length > 0).map((section) => (
              <section key={section.title}>
                <h2 className='mb-3 text-sm font-semibold text-domino-inkSoft'>{section.title}</h2>
                <div className='divide-y divide-domino-cardBorder rounded-2xl border border-domino-cardBorder bg-domino-white'>
                  {section.goals.map((goal) => (
                    <article key={goal.id} className='p-5 sm:p-6'>
                      <div className='flex items-start justify-between gap-4'>
                        <h3 className='text-base font-semibold text-domino-ink'>{goal.title}</h3>
                        {canEditWorkspaceObjective(goal, userId, isManager) && (
                          <button
                            type='button'
                            aria-label={`Modifier ${goal.title}`}
                            onClick={() => setEditing(goal)}
                            className='rounded-lg p-2 text-domino-inkSoft hover:bg-domino-muted focus-visible:ring-2 focus-visible:ring-domino-prune'
                          >
                            <Pencil className='h-4 w-4' />
                          </button>
                        )}
                      </div>
                      {goal.success_criteria.trim() !== goal.title.trim() && <p className='mt-2 text-sm leading-6 text-domino-inkSoft'>
                        {goal.success_criteria}
                      </p>}
                      <p className='mt-3 text-xs leading-5 text-domino-inkSoft'>
                        {[
                          goal.scope === 'individual' ? nameOf(goal.owner_user_id) : null,
                          goal.owner_user_id !== goal.created_by ? `Défini par ${nameOf(goal.created_by)}` : null,
                          goal.due_date ? `Échéance ${new Date(`${goal.due_date}T12:00:00`).toLocaleDateString('fr-FR')}` : null,
                          goal.status === 'active' ? null : goal.status === 'achieved' ? 'Atteint' : 'Archivé',
                        ].filter(Boolean).join(' · ')}
                      </p>
                      {(() => {
                        const measure = measures[goal.id];
                        return measure ? (
                          <div className='mt-3 max-w-md'>
                            <ObjectiveProgressLine objective={goal} measure={measure} progress={progress[goal.id]} />
                          </div>
                        ) : null;
                      })()}
                      {(() => {
                        const contributions = contributionsFor(goal);
                        if (contributionsUnavailable) return null;
                        return (
                          <div className='mt-4 border-t border-domino-cardBorder pt-4'>
                            <p className='text-sm font-semibold text-domino-ink'>Travaux reliés à cet objectif</p>
                            {contributions.length > 0 ? (
                              <ul className='mt-2 space-y-3'>
                                {contributions.map(({ subject, work }) => (
                                  <li key={`${subject.subjectId}:${work.id}`} className='text-sm'>
                                    <Link to={`/threads/${encodeURIComponent(subject.subjectId)}`} className='font-medium text-domino-aubergine underline underline-offset-4'>{subject.subjectLabel}</Link>
                                    <p className='mt-0.5 leading-6 text-domino-inkSoft'>{sweepBusinessVocabulary(work.title)}</p>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className='mt-2 text-sm leading-6 text-domino-inkSoft'>Aucun compte n’est encore explicitement relié à cet objectif.</p>
                            )}
                          </div>
                        );
                      })()}
                      {canEditWorkspaceObjective(goal, userId, isManager) && (
                        <div className='mt-4 flex flex-wrap gap-3'>
                          {goal.status === 'active'
                            ? (
                              <>
                                <Button
                                  size='sm'
                                  variant='secondary'
                                  disabled={savingId === goal.id}
                                  onClick={() => void updateStatus(goal, 'achieved')}
                                >
                                  Marquer comme atteint
                                </Button>
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  disabled={savingId === goal.id}
                                  onClick={() => void updateStatus(goal, 'archived')}
                                >
                                  Archiver
                                </Button>
                              </>
                            )
                            : (
                              <Button
                                size='sm'
                                variant='secondary'
                                disabled={savingId === goal.id}
                                onClick={() => void updateStatus(goal, 'active')}
                              >
                                Remettre en cours
                              </Button>
                            )}
                        </div>
                      )}
                      <ObjectiveChanges key={goal.updated_at} objectiveId={goal.id} />
                    </article>
                  ))}
                </div>
              </section>
            ))}
            {visible.length === 0 && (
              <p className='border-t border-domino-cardBorder py-10 text-sm text-domino-inkSoft'>
                {showHistory
                  ? 'Aucun objectif terminé ou archivé.'
                  : 'Aucun objectif défini pour le moment. Vous pouvez préciser le résultat à atteindre et son critère de réussite.'}
              </p>
            )}
          </div>
        )}
    </div>
  );
};
